import { cn } from "@/lib/utils"

export const NOTIFICATION_DETAIL_PRIMARY_FONT = "var(--font-primary)"

/** Page title — matches report detail card headings. */
export const NOTIFICATION_DETAIL_TITLE_PAGE_CLASS =
  "font-[family-name:var(--font-primary)] text-2xl font-normal leading-snug text-[color:var(--brand-tropical-surf)] md:text-3xl"

/** Side pane / modal title — smaller inset detail header. */
export const NOTIFICATION_DETAIL_TITLE_PANE_CLASS =
  "font-[family-name:var(--font-primary)] text-lg font-normal leading-snug text-[color:var(--brand-tropical-surf)]"

/** Section labels — matches report/strategy overview fields. */
export const NOTIFICATION_DETAIL_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]"

/** Body copy and field values. */
export const NOTIFICATION_DETAIL_BODY_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"

/** Rich message HTML wrapper. */
export const NOTIFICATION_DETAIL_MESSAGE_CLASS = cn(
  NOTIFICATION_DETAIL_BODY_CLASS,
  "leading-normal [&_p]:m-0 [&_p+p]:mt-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5",
  "[&_a]:text-[color:var(--brand-tropical-surf)] [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:opacity-90"
)

/** Default type badge — matches report type badges. */
export const NOTIFICATION_TYPE_BADGE_CLASS =
  "border-gray-500 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-gray-500 shadow-none dark:border-sidebar-muted-foreground dark:text-sidebar-muted-foreground"

/** Performance notifications — primary gradient badge. */
export const NOTIFICATION_TYPE_BADGE_PERFORMANCE_CLASS =
  "border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-white shadow-none bg-[image:var(--gradient-primary)]"

/** System notifications — glacial orange gradient (alert / connectivity). */
export const NOTIFICATION_TYPE_BADGE_SYSTEM_CLASS =
  "border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-white shadow-none bg-[image:var(--gradient-glacial-orange)]"

/** Billing notifications — tropical surf fill. */
export const NOTIFICATION_TYPE_BADGE_BILLING_CLASS =
  "border-0 bg-[color:var(--brand-tropical-surf)] font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-white shadow-none"

/** Reports notifications — primary gradient (same family as performance). */
export const NOTIFICATION_TYPE_BADGE_REPORTS_CLASS =
  "border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-white shadow-none bg-[image:var(--gradient-primary)]"

/** Announcement / platform update — glacial green (news / broadcast). */
export const NOTIFICATION_TYPE_BADGE_ANNOUNCEMENT_CLASS =
  "border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-white shadow-none bg-[image:var(--gradient-glacial-green)]"

/**
 * Text colour matching a type's badge, flattened to the gradient's solid stop.
 * `!` beats the `text-foreground` in shells like the metric tooltip chrome.
 */
export function notificationTypeAccentTextClass(type: string): string {
  if (type === "performance" || type === "reports" || type === "billing") {
    return "!text-[color:var(--brand-tropical-surf)]"
  }
  if (type === "system") return "!text-[color:var(--brand-glacial-orange)]"
  if (type === "announcement") return "!text-[color:var(--brand-glacial-green)]"
  return "!text-gray-500 dark:!text-white"
}

export function formatNotificationTypeLabel(type: string): string {
  return type
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

export function notificationTypeBadgeClass(type: string): string {
  if (type === "performance") return NOTIFICATION_TYPE_BADGE_PERFORMANCE_CLASS
  if (type === "system") return NOTIFICATION_TYPE_BADGE_SYSTEM_CLASS
  if (type === "billing") return NOTIFICATION_TYPE_BADGE_BILLING_CLASS
  if (type === "reports") return NOTIFICATION_TYPE_BADGE_REPORTS_CLASS
  if (type === "announcement") return NOTIFICATION_TYPE_BADGE_ANNOUNCEMENT_CLASS
  return NOTIFICATION_TYPE_BADGE_CLASS
}

export function formatNotificationDetailKey(key: string): string {
  const overrides: Record<string, string> = {
    action_url: "Statistics",
    actionUrl: "Statistics",
    reports_url: "Report",
    reportsUrl: "Report",
  }
  if (overrides[key]) return overrides[key]

  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}
